import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AdminLayout from "@/components/AdminLayout";
import { api, fileUrl } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Clock, CheckCircle2, RotateCcw, MapPin, Loader2, Save } from "lucide-react";

/**
 * AdminSourcingTripDetail — the detail view for one sourcing trip.
 * Shows every piece linked to the trip, roll-up totals, editable label /
 * notes / receipt amount, and an open/close action.
 */
export default function AdminSourcingTripDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [state, setState] = useState(null);   // { trip, items } | null
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ label: "", notes: "", total_receipt_amount: 0 });

  const load = async () => {
    try {
      const { data } = await api.get(`/sourcing-trips/${id}`);
      setState(data);
      setForm({
        label: data.trip.label,
        notes: data.trip.notes || "",
        total_receipt_amount: data.trip.total_receipt_amount || 0,
      });
    } catch (e) {
      toast.error(`could not load trip: ${e.response?.data?.detail || e.message}`);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/sourcing-trips/${id}`, form);
      toast.success("trip updated.");
      await load();
    } catch (e) {
      toast.error(`save failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const close = async () => {
    if (!window.confirm("close this trip? new inventory items will no longer auto-link to it.")) return;
    try {
      await api.post(`/sourcing-trips/${id}/close`);
      toast.success("trip closed.");
      await load();
    } catch (e) {
      toast.error(`could not close: ${e.response?.data?.detail || e.message}`);
    }
  };

  const reopen = async () => {
    try {
      await api.post(`/sourcing-trips/${id}/reopen`);
      toast.success("trip reopened. any other open trip was closed automatically.");
      await load();
    } catch (e) {
      toast.error(`could not reopen: ${e.response?.data?.detail || e.message}`);
    }
  };

  if (!state) return <AdminLayout><div className="p-10 text-taupe text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> loading…</div></AdminLayout>;

  const { trip, items } = state;
  const totalSpent = items.reduce((n, i) => n + Number(i.purchase_cost || 0) + Number(i.restoration_cost || 0), 0);
  const soldItems = items.filter((i) => i.sold);
  const revenue = soldItems.reduce((n, i) => n + Number(i.sold_price || 0) - Number(i.sale_refund_amount || 0), 0);
  const fees = soldItems.reduce((n, i) => n + Number(i.sale_payment_processing_fee || 0) + Number(i.sale_shipping_label_cost || 0), 0);
  const net = revenue - totalSpent - fees;

  return (
    <AdminLayout>
      <div className="p-6 sm:p-10 space-y-6 max-w-6xl">
        <button onClick={() => nav("/admin/sourcing-trips")} className="text-xs tracking-widest uppercase text-taupe hover:text-charcoal inline-flex items-center gap-1" data-testid="back-to-trips">
          <ArrowLeft className="w-3 h-3" /> back to trips
        </button>

        <header className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="editorial-label mb-1 flex items-center gap-2">
              <MapPin className="w-3 h-3 text-brass" /> trip · {trip.is_open ? "open" : "closed"}
            </p>
            <h1 className="font-serif text-4xl text-charcoal">{trip.label}</h1>
            <p className="text-espresso text-xs mt-1">
              started {new Date(trip.started_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}
              {trip.ended_at && ` · closed ${new Date(trip.ended_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}`}
            </p>
          </div>
          <div className="flex gap-2">
            {trip.is_open ? (
              <button onClick={close} className="border border-taupe hover:border-charcoal px-4 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1" data-testid="close-trip-btn">
                <CheckCircle2 className="w-3 h-3" /> close trip
              </button>
            ) : (
              <button onClick={reopen} className="border border-brass text-brass hover:bg-brass hover:text-ivory px-4 py-2 text-[0.65rem] tracking-widest uppercase flex items-center gap-1" data-testid="reopen-trip-btn">
                <RotateCcw className="w-3 h-3" /> reopen
              </button>
            )}
          </div>
        </header>

        {/* Roll-up */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          <Stat label="pieces" value={items.length} />
          <Stat label="sold" value={soldItems.length} />
          <Stat label="spent" value={`$${totalSpent.toFixed(2)}`} />
          <Stat label="revenue" value={`$${revenue.toFixed(2)}`} />
          <Stat label="net profit" value={`$${net.toFixed(2)}`} tone={net >= 0 ? "positive" : "negative"} />
        </div>

        {/* Editable metadata */}
        <div className="border border-taupe/40 bg-ivory p-5 space-y-4" data-testid="trip-meta-form">
          <div>
            <label className="editorial-label">label</label>
            <input
              value={form.label}
              onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
              className="w-full border-b border-taupe/60 focus:border-charcoal bg-transparent py-1.5 outline-none"
              data-testid="trip-label-input"
            />
          </div>
          <div>
            <label className="editorial-label">total receipt amount ($)</label>
            <input
              type="number"
              step="0.01"
              value={form.total_receipt_amount}
              onChange={(e) => setForm((f) => ({ ...f, total_receipt_amount: Number(e.target.value) }))}
              className="w-full border-b border-taupe/60 focus:border-charcoal bg-transparent py-1.5 outline-none"
              data-testid="trip-receipt-input"
            />
            <p className="text-[0.6rem] tracking-widest uppercase text-taupe mt-1">
              what you actually paid at checkout — useful when the receipt covers multiple pieces you haven't split-costed yet
            </p>
          </div>
          <div>
            <label className="editorial-label">notes</label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full border border-taupe/60 focus:border-charcoal bg-transparent p-2 outline-none text-sm"
              placeholder="observations for next time — good early bird, quiet weekday, staff friendly, etc."
              data-testid="trip-notes-input"
            />
          </div>
          <button
            onClick={save}
            disabled={saving}
            className="border border-brass bg-brass text-ivory hover:bg-charcoal disabled:opacity-40 px-4 py-2 text-[0.65rem] tracking-widest uppercase inline-flex items-center gap-2"
            data-testid="trip-save-btn"
          >
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} save
          </button>
        </div>

        {/* Pieces */}
        <div>
          <p className="editorial-label mb-3">pieces on this trip · {items.length}</p>
          {items.length === 0 ? (
            <div className="border border-taupe/40 p-8 text-center text-taupe text-sm">
              {trip.is_open
                ? "no pieces yet. add inventory from the inventory page — they'll auto-link to this trip."
                : "this trip had no pieces recorded."}
            </div>
          ) : (
            <div className="grid gap-2">
              {items.map((it) => (
                <Link
                  key={it.id}
                  to={`/admin/inventory/${it.id}`}
                  className="border border-taupe/40 bg-ivory p-3 hover:border-brass flex items-center gap-3"
                  data-testid={`trip-item-${it.id}`}
                >
                  {it.photos?.[0] ? (
                    <img src={fileUrl(it.photos[0])} alt="" className="w-12 h-12 object-cover border border-taupe/30 flex-shrink-0" />
                  ) : (
                    <div className="w-12 h-12 border border-taupe/30 bg-linen flex-shrink-0" />
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-charcoal truncate">{it.title || `sku ${it.sku}`}</p>
                    <p className="text-xs text-taupe">sku {it.sku} · ${Number(it.purchase_cost || 0).toFixed(2)} paid{it.sold ? ` · sold $${Number(it.sold_price || 0).toFixed(2)}` : ""}</p>
                  </div>
                  {it.sold && <span className="text-[0.55rem] tracking-widest uppercase text-emerald-800 bg-emerald-100 px-1.5 py-0.5">sold</span>}
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}

function Stat({ label, value, tone }) {
  const cls = {
    positive: "border-emerald-500/60 bg-emerald-50 text-emerald-800",
    negative: "border-red-500/50 bg-red-50 text-red-800",
  }[tone] || "border-taupe/50 bg-ivory text-charcoal";
  return (
    <div className={`border p-3 ${cls}`}>
      <p className="font-serif text-xl">{value}</p>
      <p className="text-[0.6rem] tracking-widest uppercase mt-0.5 text-taupe">{label}</p>
    </div>
  );
}
